import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi } from "ag-grid-community";

import {
  TextButton,
  IconAddCircle,
  SideSheetMainWrapper,
  SideSheetOverlay,
  SideSheetBodyContainer,
} from "@cdkglobal/radial";

import RenderCorrelationAGGrid from "./RenderCorrelationAgGrid";
import { FormCellRenderer, RowActionsRenderer } from "./renderers";
import {
  JsonSchemaItems,
  RenderGridCoRelationRowsProps,
  ErrorState,
  RowScopedErrorsState,
  RowDataObject,
  RowDataValueType,
} from "../../../../../types";
import RecurringEntryRowSideSheet from "./RenderCorrelationRowSideSheet";
import { exportCorelationRecordsToExcel, isNewRecord } from "../../../helpers";
import { useUserContext } from "../../../../../contexts/UserContext";
import { getIsRecordChanged } from "../../../validations";
import { useUIStateContext } from "../../../../../contexts/UIStateContext";
import {
  CORRELATION_GRID_COLUMNS_BY_PROPERTY_KEY,
  TOAST_MESSAGES,
} from "../../../constants";
import { processDependenyRules } from "../../../evaluations";
import CorrelationFileUpload from "./CorrelationFileUploadDialog";
import { getFormattedDisplayLabel } from "../../../helpers/importRjeReference";

export default function RenderCorrelationTableRows({
  inputValueObject,
  rows,
  propertyKey,
  property,
  isDeleteButtonDisabled,
  isAddButtonDisabled,
  isReadOnly,
  rowDataReducerState,
  dependencyRules,
  renderInputType,
  addRow,
  deleteRow,
  title,
  dispatch,
  tableHeight,
}: Readonly<RenderGridCoRelationRowsProps>): JSX.Element {
  const { selected, iamUserState } = useUserContext();
  const {
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    setRecordPageToast,
  } = useUIStateContext();
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [isSideSheetCollapsed, setIsSideSheetCollapsed] = useState(true);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [displayedRowCount, setDisplayedRowCount] = useState(rows.length);

  const { schema, rowData } = rowDataReducerState;
  const setupName = schema.title;
  const isRecordChanged = getIsRecordChanged({
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    rowDataReducerState,
  });

  const isNewEntryRecord = isNewRecord(rowData);

  const propertyItems = property.items as JsonSchemaItems;
  const { properties: corelationFieldProperties = {} } = propertyItems;

  const MAX_CORRELATION_GRID_FIELDS = 7;
  const defaultCorrelationGridFields = Object.keys(corelationFieldProperties)
    .filter((key) => !corelationFieldProperties[key].isHidden)
    .slice(0, MAX_CORRELATION_GRID_FIELDS);

  const correlationGridFields =
    CORRELATION_GRID_COLUMNS_BY_PROPERTY_KEY.get(propertyKey) ??
    defaultCorrelationGridFields;

  const getFilteredRows = () => {
    const filteredRows: RowDataObject[] = [];

    gridApi?.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) filteredRows.push(node.data);
    });

    return filteredRows;
  };
  const openSideSheet = (rowIndex: number) => {
    setSelectedRowIndex(rowIndex);
    setIsSideSheetCollapsed(false);
  };

  const closeSideSheet = () => {
    setIsSideSheetCollapsed(true);
    setSelectedRowIndex(null);
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValue(event.target.value);
  };

  // Errors for this property, narrowed to the row-scoped shape
  const rowErrorsByIndex = (rowDataReducerState.rowDataErrors?.[propertyKey] ??
    {}) as RowScopedErrorsState;

  const getRowErrorCount = (rowIndex: number): number => {
    const rowErrorEntry = rowErrorsByIndex[rowIndex];

    if (!rowErrorEntry) {
      return 0;
    }

    return Object.values(rowErrorEntry).filter(
      (fieldError: ErrorState) => fieldError?.error,
    ).length;
  };

  const rowHasError = (rowIndex: number): boolean =>
    getRowErrorCount(rowIndex) > 0;

  const errorRowCount = useMemo(
    () =>
      rows.reduce(
        (count, _id, rowIndex) => count + (rowHasError(rowIndex) ? 1 : 0),
        0,
      ),
    [rows, rowErrorsByIndex],
  );

  const getFieldDisplayValue = useCallback(
    (fieldName: string, fieldValue: RowDataValueType) =>
      getFormattedDisplayLabel(
        fieldName,
        fieldValue,
        corelationFieldProperties,
        setupName,
        iamUserState,
      ),
    [corelationFieldProperties, setupName, iamUserState],
  );

  const gridRowData = useMemo(() => {
    const data = rows.map((id, rowIndex) => ({
      id,
      rowIndex,
      errorCount: getRowErrorCount(rowIndex),
      ...inputValueObject[rowIndex],
    }));

    return showErrorsOnly ? data.filter((row) => row.errorCount > 0) : data;
  }, [rows, rowErrorsByIndex, showErrorsOnly]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const columns: ColDef[] = Object.keys(corelationFieldProperties)
      .filter((propertySubKey) => {
        const fieldConfig = corelationFieldProperties[propertySubKey];
        return (
          !fieldConfig?.isHidden &&
          correlationGridFields.includes(propertySubKey)
        );
      })
      .map((propertySubKey) => {
        const item = corelationFieldProperties[propertySubKey];

        return {
          field: propertySubKey,
          headerName: item.title ?? propertySubKey,
          flex: 1,
          sortable: true,
          filter: true,
          cellRenderer: FormCellRenderer,
          valueGetter: (params) =>
            getFieldDisplayValue(propertySubKey, params.data[propertySubKey]),
          cellRendererParams: {
            propertySubKey,
            item,
            renderInputType,
          },
        };
      });

    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filter: false,
      valueGetter: () => "",
      cellRenderer: RowActionsRenderer,
      cellRendererParams: {
        isReadOnly,
        isDeleteButtonDisabled,
        deleteRow,
        openSideSheet,
      },
    });

    return columns;
  }, [
    corelationFieldProperties,
    renderInputType,
    deleteRow,
    isDeleteButtonDisabled,
    isReadOnly,
  ]);

  const handleExportRecords = async () => {
    if (isRecordChanged) {
      setRecordPageToast(TOAST_MESSAGES.UNSAVED_CHANGES_WARNING());
      return;
    }
    const filteredRecords = getFilteredRows();
    const fileName =
      `${selected.setup.label}_${selected.groupId.label}_${title}_${
        new Date().toISOString().split("T")[0]
      }`
        .replace(/\s*-\s*/g, "-")
        .replace(/\s+/g, "-");

    await exportCorelationRecordsToExcel({
      fileName,
      schema,
      rowData,
      corelationRows: filteredRecords,
      propertyKey,
      selected,
      iamUserState,
    });
  };

  const handleImportRecords = () => {
    if (isRecordChanged) {
      setRecordPageToast(TOAST_MESSAGES.UNSAVED_CHANGES_WARNING());
      return;
    }
    setIsUploadDialogOpen(true);
  };

  const defaultColDef: ColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      suppressKeyboardEvent: (params) => {
        const key = params.event?.key;
        if (!key) {
          return true;
        }
        return key === "Tab";
      },
    }),
    [],
  );

  useEffect(() => {
    if (!isSideSheetCollapsed) return;
    inputValueObject?.forEach((row, rowIndex) => {
      Object.entries(corelationFieldProperties)
        .filter(
          ([propertySubKey]) => !correlationGridFields.includes(propertySubKey),
        )
        .forEach(([propertySubKey]) => {
          if (dependencyRules) {
            processDependenyRules({
              dependencyRules: dependencyRules,
              propertyKey,
              inputValue: row?.[propertySubKey] as string | null,
              propertySubKey,
              rowIndex,
              selectedTabSectionKey,
              rowDataReducerState,
              dispatch,
            });
          }
        });
    });
  }, [inputValueObject, isSideSheetCollapsed]);

  return (
    <SideSheetMainWrapper isModal>
      <SideSheetOverlay
        isCollapsed={isSideSheetCollapsed}
        onClick={closeSideSheet}
      />
      <SideSheetBodyContainer>
        <RenderCorrelationAGGrid
          propertyKey={propertyKey}
          title={title}
          searchValue={searchValue}
          displayedRowCount={displayedRowCount}
          rowsLength={rows.length}
          isReadOnly={isReadOnly}
          isNewEntryRecord={isNewEntryRecord}
          showErrorsOnly={showErrorsOnly}
          errorRowCount={errorRowCount}
          gridRowData={gridRowData}
          columnDefs={columnDefs}
          gridRef={gridRef}
          defaultColDef={defaultColDef}
          setGridApi={setGridApi}
          setDisplayedRowCount={setDisplayedRowCount}
          handleSearchInputChange={handleSearchInputChange}
          setSearchValue={setSearchValue}
          setShowErrorsOnly={setShowErrorsOnly}
          handleImportRecords={handleImportRecords}
          handleExportRecords={handleExportRecords}
          tableHeight={tableHeight}
        />
        {!isReadOnly && (
          <TextButton
            icon={<IconAddCircle />}
            dataTestId={`add-${propertyKey}-row-button`}
            onClick={addRow}
            isDisabled={isAddButtonDisabled || showErrorsOnly}
            text={rows.length === 0 ? "Add" : "Add Another"}
            style={{ margin: "0.5rem 0" }}
          />
        )}
      </SideSheetBodyContainer>

      <RecurringEntryRowSideSheet
        isSideSheetCollapsed={isSideSheetCollapsed}
        closeSideSheet={closeSideSheet}
        selectedRowIndex={selectedRowIndex}
        properties={corelationFieldProperties}
        renderInputType={renderInputType}
      />
      {isUploadDialogOpen && (
        <CorrelationFileUpload
          isOpen={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
          rowDataReducerState={rowDataReducerState}
          propertyKey={propertyKey}
          corelationFieldProperties={corelationFieldProperties}
          title={title}
          dispatch={dispatch}
          getFieldDisplayValue={getFieldDisplayValue}
        />
      )}
    </SideSheetMainWrapper>
  );
}
